import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext, CartContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { ShoppingCart, Video, Trash2, Plus, Minus, Menu, X } from 'lucide-react';

const Header = () => {
  const { user } = useContext(AuthContext);
  const { cart, removeFromCart, updateCartQuantity, cartTotal } = useContext(CartContext);
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const getDashboardRoute = () => {
    if (!user) return '/auth';
    return `/dashboard/${user.role}`;
  };

  const handleNavClick = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <header className="header-nav fixed w-full top-0 z-50" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
          <img src="https://customer-assets.emergentagent.com/job_sunkamalplay/artifacts/oha9raa2_logo-kamal.webp" alt="Sun Kamal Academy" className="h-12 sm:h-14" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex gap-6 lg:gap-8 items-center" aria-label="Main navigation">
          <Link to="/classes" className="text-gray-700 hover:text-orange-500 font-medium transition" data-testid="nav-classes">Courses</Link>
          <Link to="/schedule" className="text-gray-700 hover:text-orange-500 font-medium transition" data-testid="nav-schedule">Schedule</Link>
          <Link to="/shop" className="text-gray-700 hover:text-orange-500 font-medium transition" data-testid="nav-shop">Shop</Link>
          <a href="mailto:info@sunkamalsports.online" className="text-gray-700 hover:text-orange-500 font-medium transition">Contact</a>
        </nav>

        <div className="flex gap-2 sm:gap-3 items-center">
          {/* Zoom Class Button - hidden on small mobile */}
          <Button onClick={() => navigate('/schedule')} className="zoom-button hidden sm:inline-flex text-xs sm:text-sm" data-testid="zoom-class-btn">
            <Video className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden xs:inline">Zoom Class</span>
          </Button>

          {/* Shopping Cart */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="relative p-2 sm:p-2.5" data-testid="cart-button" aria-label="Shopping Cart">
                <ShoppingCart className="w-5 h-5" />
                {cart.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {cart.length}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:w-[400px] md:w-[540px]">
              <SheetHeader>
                <SheetTitle>Shopping Cart ({cart.length})</SheetTitle>
              </SheetHeader>
              <div className="mt-6">
                {cart.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingCart className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600">Your cart is empty</p>
                    <Button onClick={() => navigate('/shop')} className="mt-4">
                      Browse Products
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="space-y-4 max-h-[55vh] overflow-y-auto">
                      {cart.map((item) => (
                        <div key={item.id} className="flex gap-3 border-b pb-4">
                          <img src={item.images[0]} alt={item.name} className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded" />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-sm sm:text-base truncate">{item.name}</h4>
                            <p className="text-sm text-gray-600">${item.price}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                                className="h-7 w-7 p-0"
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              <span className="text-sm font-medium">{item.quantity}</span>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                                className="h-7 w-7 p-0"
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => removeFromCart(item.id)}
                                className="ml-auto h-7 w-7 p-0"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 space-y-4">
                      <div className="flex justify-between text-lg font-bold">
                        <span>Total:</span>
                        <span>${cartTotal.toFixed(2)}</span>
                      </div>
                      <Button className="w-full btn-primary" onClick={() => navigate('/shop')}>
                        Checkout
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex gap-2 items-center">
            {user ? (
              <Button onClick={() => navigate(getDashboardRoute())} className="btn-primary text-sm" data-testid="dashboard-btn">
                Dashboard
              </Button>
            ) : (
              <>
                <Button onClick={() => navigate('/auth')} variant="outline" data-testid="login-btn" className="text-sm">Login</Button>
                <Button onClick={() => navigate('/auth')} className="btn-primary text-sm" data-testid="get-started-btn">Get Started</Button>
              </>
            )}
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-gray-700 hover:text-orange-500 transition"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <nav className="md:hidden bg-white border-t shadow-lg animate-slide-down" aria-label="Mobile navigation" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            <button onClick={() => handleNavClick('/classes')} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-500 rounded-lg font-medium transition">
              Courses
            </button>
            <button onClick={() => handleNavClick('/schedule')} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-500 rounded-lg font-medium transition">
              Schedule
            </button>
            <button onClick={() => handleNavClick('/shop')} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-500 rounded-lg font-medium transition">
              Shop
            </button>
            <a href="mailto:info@sunkamalsports.online" className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-500 rounded-lg font-medium transition">
              Contact
            </a>
            <div className="border-t pt-3 mt-3 space-y-2">
              {user ? (
                <Button onClick={() => handleNavClick(getDashboardRoute())} className="w-full btn-primary" data-testid="mobile-dashboard-btn">
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button onClick={() => handleNavClick('/auth')} variant="outline" className="w-full" data-testid="mobile-login-btn">Login</Button>
                  <Button onClick={() => handleNavClick('/auth')} className="w-full btn-primary" data-testid="mobile-get-started-btn">Get Started</Button>
                </>
              )}
            </div>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
